import React from "react";
// ANT DESIGN COMPONENTS
import { Modal, Typography, Divider, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateOpenModalBankActionsHelp } from "../../../redux/banks/bankSlice";
// INTERFACES
import { RootState } from "../../../app/store";
// CONFIG
import { ACTION_TYPE_CONFIG } from "../../monitoringManagement/Actions/actionTypeConfig";

/**
 * The order types are presented in: simplest binding first, so an operator
 * reading top-to-bottom meets one command before two, and the manual "off"
 * before the automatic one.
 *
 * A `Record` over the config's keys rather than a list, so a new button type
 * fails to compile here instead of silently going undocumented — which is
 * exactly what happened to ON_OFF, SINGLE and ONE_SHOT under the previous
 * hand-written version of this modal.
 */
const GUIDE_ORDER: Record<keyof typeof ACTION_TYPE_CONFIG, number> = {
  SINGLE: 1,
  ON_OFF: 2,
  ONE_SHOT: 3,
  TOGGLE: 4,
  HOLDBACK: 5,
  TURNBACK: 6,
  TOGGLETIMER: 7,
  ENERGIZER_TURN_BACK: 8,
  SWITCH: 9,
};

/**
 * The Action Button Types user guide.
 *
 * Rendered FROM `ACTION_TYPE_CONFIG` rather than hand-written. That config's
 * own comment already declared this modal's copy to be the same copy the
 * monitoring cards show — "an operator configuring an action and an operator
 * running it were reading two different descriptions of the same interaction"
 * — but the modal was never actually converted, so it stayed a hardcoded list
 * of six sections while the config grew to nine.
 *
 * The visible cost of that: ON_OFF, SINGLE and ONE_SHOT were absent from the
 * guide entirely, including ONE_SHOT, which is what every auto-seeded call
 * registration now uses. Generating the list means the guide cannot fall behind
 * the types again — a new type appears here the moment it is configured.
 */
function BankActionsHelpModal() {
  const dispatch = useDispatch();
  const { Title, Paragraph, Text } = Typography;

  const { openModalBankActionsHelp } = useSelector((state: RootState) => state.bank);

  const onClickCancel = () => {
    dispatch(updateOpenModalBankActionsHelp(false));
  };

  const types = (Object.keys(ACTION_TYPE_CONFIG) as Array<keyof typeof ACTION_TYPE_CONFIG>).sort(
    (a, b) => GUIDE_ORDER[a] - GUIDE_ORDER[b],
  );

  return (
    <Modal
      open={openModalBankActionsHelp}
      width="50%"
      onOk={onClickCancel}
      onCancel={onClickCancel}
      maskClosable={false}
    >
      <div style={{ padding: 16 }}>
        <Title level={3}>Action Button Types - User Guide</Title>
        <Paragraph type="secondary">
          The button type decides how many buttons a row shows in the monitoring Control Panel and
          what each one sends. It is not cosmetic: a type that expects a paired command will render
          a second button, and a row bound to only one command has nothing for that button to send.
        </Paragraph>

        {types.map((type, index) => {
          const { label, guide, accent } = ACTION_TYPE_CONFIG[type];

          return (
            <div key={type}>
              {index > 0 && <Divider />}
              <Title level={4}>
                <Tag color={accent}>{type}</Tag> {label}
              </Title>
              <Paragraph>
                {guide.intro.text}
                {guide.bullets && guide.bullets.length > 0 && (
                  <ul>
                    {guide.bullets.map((bullet) => (
                      <li key={bullet.key}>{bullet.text}</li>
                    ))}
                  </ul>
                )}
                {guide.outro && <Text>{guide.outro.text}</Text>}
              </Paragraph>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export default BankActionsHelpModal;
